import { apiRequest } from "@shared/services/api/apiRequest";
import { orgInvitationEventBus } from "../orgInvitation.events";
import { OrganizationType } from "@platform/types/orgInvitation.types";

type Args = {
  code?: string;
  organization?: OrganizationType;
  type: "lookupOrg" | "code";
};

/**
 * Validate an invitation code either with a code (coming from the code input)
 * or with an organization (coming from the lookup input).
 * Emit events based on the type of validation.
 * */
export const validateOrgInvitation = async ({
  code,
  organization,
  type,
}: Args) => {
  // If the type is lookupOrg, the organization is required
  if (type === "lookupOrg" && !organization) {
    throw new Error("Organization is required");
  }

  // If the type is code, the code is required
  if (type === "code" && !code) {
    throw new Error("Code is required");
  }

  // api expects a code so we need to get the code from the organization if the type is lookupOrg
  const codeToValidate = type === "code" ? code : organization?.invitation_code;

  // Emit events based on the type of validation
  type === "lookupOrg" &&
    orgInvitationEventBus.emitEvent("lookupOrg:submitted", {
      organization: organization!,
    });
  type === "code" &&
    orgInvitationEventBus.emitEvent("code:submitted", { code: code! });

  try {
    const response = await apiRequest(
      `/api/get-invitation/?code=${codeToValidate}`,
      "GET",
      null,
      false
    );

    // Emit events based on the type of validation
    type === "lookupOrg" &&
      orgInvitationEventBus.emitEvent("lookupOrg:success", {
        organization: organization!,
        response,
      });
    type === "code" &&
      orgInvitationEventBus.emitEvent("code:success", {
        code: code!,
        response,
      });

    return response;
  } catch (error) {
    // Emit events based on the type of validation
    type === "lookupOrg" &&
      orgInvitationEventBus.emitEvent("lookupOrg:failed", {
        organization: organization!,
      });
    type === "code" &&
      orgInvitationEventBus.emitEvent("code:failed", { code: code! });

    throw error;
  }
};

export default validateOrgInvitation;
